import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

export interface EditApprovalChainWarningDialogProps {
  open: boolean;
  /** Number of assets currently using the approval chain (drives the copy). */
  affectedAssetCount: number;
  /**
   * Whether the edit flips the chain between in-order and any-order. That discards
   * every approval on the chain, which the plain "affects in-progress approvals"
   * line understates.
   */
  changesExecutionMode?: boolean;
  onCancel: () => void;
  onProceed: () => void;
}

/**
 * Shown before editing an approval chain that is currently in use.
 *
 * Per the approval design, editing an active approval chain takes effect immediately
 * on in-progress approvals, so the user must acknowledge the impact before we
 * navigate to the editor. When the approval chain is unused the caller skips this
 * dialog entirely and navigates straight through.
 */
export const EditApprovalChainWarningDialog = ({
  open,
  affectedAssetCount,
  changesExecutionMode = false,
  onCancel,
  onProceed,
}: EditApprovalChainWarningDialogProps) => {
  const { t } = useTranslation('approvalChains');

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="gap-0 p-4 max-w-120"
        data-testid="edit-approval-chain-warning-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t('manageApprovalChains.editApprovalChainWarningDialog.editThisApprovalChain')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 pb-10 pt-4">
          <p className="text-foreground-alt">
            {t('manageApprovalChains.editApprovalChainWarningDialog.currentlyActiveOnAssets', {
              count: affectedAssetCount,
            })}
          </p>
          {changesExecutionMode && (
            <p className="text-destructive" data-testid="edit-approval-chain-mode-warning">
              {t(
                'manageApprovalChains.editApprovalChainWarningDialog.changingTheExecutionOrderRestarts',
              )}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('manageApprovalChains.editApprovalChainWarningDialog.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onProceed}
            data-testid="edit-approval-chain-warning-proceed"
          >
            {t('manageApprovalChains.editApprovalChainWarningDialog.proceed')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

EditApprovalChainWarningDialog.displayName = 'EditApprovalChainWarningDialog';
